import React, { useCallback, useRef, useState } from 'react';
import { useStyles } from './PagesThumbnails.styles';
import ProtectedImg from '../ProtectedImg/ProtectedImg.component';
import { thumbNailPage } from './PagesThumbnails.types';
import ActionsMenu from '../ActionsMenu/ActionsMenu.component';
import { ButtonOne } from '../ButtonOne/ButtonOne.component';
import { Grid } from '@mui/material';
import { Prompt, PromptProps } from '../Prompt/Prompt.component';
import { useLayoutContext } from '../../Context/Layout.context';
import { replacePageImage } from '../../services/images/images.service';
import { extractFileType, FileType, getMaxFileSizeInBytes } from '../../utils/fileProcessing';

const acceptedFileType: FileType = 'image/png';

interface PagesThumbnailsProps {
  pages: thumbNailPage[];
  currentPageNo?: number;
  currentPageId?: string | number;
  currentPageIndex?: number;
  thumbnailWidth: string;
  needsThumbnailReload?: boolean;
  onClickPage: (pageNo: number, pageIndex: number, pageId: string | number) => void;
  onThumbnailReload?: () => void;
  actions?: boolean;
  onFileValidated?: (file: Blob, index: number) => void;
  onFileReplacementCancel?: (index: number) => void;
  onUploadStarted?: (index: number) => void;
  onFileSaved?: (index: number) => void;
}

const PagesThumbnails: React.FC<PagesThumbnailsProps> = (props) => {
  const classes = useStyles();
  const { openAlert } = useLayoutContext();
  const [hooveringOverPage, setHooveringOverPage] = useState<number>();
  const [pageIndexToBeReplaced, setPageIndexToBeReplaced] = useState<number>();
  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const [fileValidationErrorPrompt, setFileValidationErrorPrompt] = useState<PromptProps>({
    type: 'error',
    open: false,
  });

  const displayError = (type: 'format' | 'size') => {
    setFileValidationErrorPrompt((prompt) => {
      return {
        ...prompt,
        open: true,
        mainText: type === 'format' ? 'Wybrany plik ma nieprawidłowy format' : 'Przesyłany plik jest za duży',
        subTexts: [{ text: type === 'format' ? 'Dopuszczalny format to png' : 'Maksymalna wielkość pliku to 2 MB' }],
        onConfirm: () => setFileValidationErrorPrompt({ type: 'error', open: false }),
      };
    });
  };

  const isValid = async (file: File): Promise<boolean> => {
    if (
      file.type !== acceptedFileType ||
      (await file.arrayBuffer().then((buffer) => extractFileType(buffer) !== acceptedFileType))
    ) {
      displayError('format');
      return false;
    }
    if (file.size > getMaxFileSizeInBytes(2)) {
      displayError('size');
      return false;
    }
    return true;
  };

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      pageIndexToBeReplaced !== undefined &&
      event.target.files &&
      event.target.files.length === 1 &&
      props.onFileValidated
    ) {
      const file = event.target.files[0];
      isValid(file).then((valid) => {
        if (valid && props.onFileValidated) {
          props.onFileValidated(file, pageIndexToBeReplaced);
          event.stopPropagation();
          event.preventDefault();
        }
      });
    }
  };

  const handleCancel = () => {
    if (props.onFileReplacementCancel && pageIndexToBeReplaced !== undefined) {
      props.onFileReplacementCancel(pageIndexToBeReplaced);
      setPageIndexToBeReplaced(undefined);
    }
  };

  const handleError = () => {
    if (props.onFileReplacementCancel && pageIndexToBeReplaced !== undefined) {
      props.onFileReplacementCancel(pageIndexToBeReplaced);
      openAlert('error');
    }
  };

  const handleSave = (item: thumbNailPage) => {
    if (pageIndexToBeReplaced !== undefined && item.urlToUpdate && item.newImgData) {
      if (props.onUploadStarted) props.onUploadStarted(pageIndexToBeReplaced);
      replacePageImage(item.urlToUpdate, item.newImgData)
        .then(
          (result) => {
            if (result.data) {
              if (props.onFileSaved) props.onFileSaved(pageIndexToBeReplaced);
              openAlert('success');
            } else {
              handleError();
            }
          },
          () => {
            handleError();
          },
        )
        .catch((e) => {
          handleError();
        })
        .finally(() => {
          setPageIndexToBeReplaced(undefined);
        });
    }
  };

  const initPageReplacement = useCallback(
    (index: number) => {
      if (inputFileRef && inputFileRef.current) {
        setPageIndexToBeReplaced(index);
        inputFileRef.current.click();
      }
    },
    [inputFileRef],
  );

  return (
    <div>
      {props.pages.map((item: thumbNailPage, index: number) => {
        const isActive =
          props.currentPageNo === index + 1 || props.currentPageIndex === index || props.currentPageId === item.id;
        return (
          <React.Fragment key={item.id}>
            <div className={classes.cardThumbnailCapition}>{item.caption}</div>
            <div
              className={classes.thumbnailContainer}
              onMouseEnter={() => setHooveringOverPage(index)}
              onMouseLeave={() => setHooveringOverPage(undefined)}
            >
              {props.actions && isActive && hooveringOverPage === index && (
                <div className={classes.actions} onClick={() => props.onClickPage(index + 1, index, item.id)}>
                  <ActionsMenu
                    menuItems={[
                      {
                        text: 'Podmień obraz karty',
                        onClick: () => initPageReplacement(index),
                      },
                    ]}
                  />
                </div>
              )}
              <span
                onKeyPress={(event: React.KeyboardEvent) => {
                  if (event.key === 'Enter') {
                    props.onClickPage(index + 1, index, item.id);
                  }
                }}
                tabIndex={0}
              >
                <ProtectedImg
                  className={
                    isActive ? [classes.cardThumbnail, classes.cardThumbnailActive].join(' ') : classes.cardThumbnail
                  }
                  style={{ width: props.thumbnailWidth, opacity: item.uploadInProgress ? '0.7' : '1' }}
                  src={item.url}
                  alt={`Miniatura strona ${index + 1} `}
                  imgData={item.newImgData ? item.newImgData : item.imgData}
                  onClick={() => props.onClickPage(index + 1, index, item.id)}
                  forceReload={isActive ? props.needsThumbnailReload : undefined}
                  onLoad={isActive && props.onThumbnailReload ? props.onThumbnailReload : undefined}
                  uploadInProgress={item.uploadInProgress}
                />
              </span>
              {pageIndexToBeReplaced === index && item.newImgData && (
                <Grid container spacing={1} justifyContent="flex-end">
                  <Grid item>
                    <ButtonOne color="primary" variant="outlined" onClick={handleCancel}>
                      ANULUJ
                    </ButtonOne>
                  </Grid>
                  <Grid item>
                    <ButtonOne color="primary" variant="contained" onClick={() => handleSave(item)}>
                      ZAPISZ
                    </ButtonOne>
                  </Grid>
                </Grid>
              )}
            </div>
          </React.Fragment>
        );
      })}
      <input
        type="file"
        ref={inputFileRef}
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFile(e);
          e.target.value = '';
        }}
      />
      {fileValidationErrorPrompt.open && <Prompt {...fileValidationErrorPrompt} />}
    </div>
  );
};

export default React.memo(PagesThumbnails);
